import { OrderModel, PositionModel } from "@trading/core";

export const enum PrivateConnectionChannels {
  ORDER = 'orders',
  HISTORY = 'trades', // history
  POSITION = 'positions',
  BALANCES = 'balances'
}

export const enum PublickConnectionChannels {
  BEST_BID_OFFER = 'bbo',
  ORDER_BOOK = 'orderbook',
  KLINES = 'klines',
  TRADES = 'trades'
}

export type AvailableChannels = PrivateConnectionChannels | PublickConnectionChannels

// то что уходит в action
export type ChanelActionType = 'subscribe' | 'connected' | 'auth' | 'update' | 'snapshot'

// то что приходит в type
export type ChanelMessageType = 'subscribed' | 'connect' | 'authenticated' | 'updated'

export interface IncomingMessagePayloadError {
  channel?: PrivateConnectionChannels
  type: 'error'
  error?: string
}

export interface IncomingMessagePayloadSuccess<T = unknown> {
  channel?: string
  type: ChanelActionType
  data: T
}

export type IncomingMessagePayload<T> =
  | IncomingMessagePayloadSuccess<T>
  | IncomingMessagePayloadError

export interface PingSendPayload {
  action: 'ping'
  timestamp: number
}

export interface AuthSendPayload {
  action: 'auth'
  token?: string
}

export interface SubscribeSendPayload {
  action: 'subscribe'
  channel: AvailableChannels
}

export interface UnsubscribeSendPayload {
  action: 'unsubscribe'
  channel: AvailableChannels
}

export interface UpdateData {
  action: 'update'
  data: unknown
  channel: AvailableChannels
}

export type SendMessageTypes =
  | AuthSendPayload
  | SubscribeSendPayload
  | UnsubscribeSendPayload
  | UpdateData
  | PingSendPayload

export interface IWebSocketConnection {
  isConnected: boolean

  sendMessage(payload: SendMessageTypes): void

  subscribeToChannelMessages(
    channel: AvailableChannels,
    options?: Record<string, unknown>
  ): () => void

  unsubscribeToChannelMessages(channel: AvailableChannels, options?: Record<string, unknown>): void

  // Подписка на событие (возвращает функцию отписки)
  addChannelHandler<T>(
    event: string,
    handler: (data: IncomingMessagePayload<T>) => void
  ): () => void

  auth(): void

  close(): void
}

export abstract class RepositoryWS<T> {
  public abstract subscribeToChannelsAction(
    actionName: ChanelActionType,
    cb: (data: T) => void
  ): Promise<undefined | ReturnType<IWebSocketConnection['subscribeToChannelMessages']>>

  public abstract unsubscribeToChannel(): void
}

export interface PositionWSUpdate {
  event: 'created' | 'updated' | 'closed' | 'deleted'
  position: PositionModel
}

export interface OrderWSUpdate {
  event: 'created' | 'filled' | 'cancelled' | 'partially_filled'
  order: OrderModel
}

export interface TradeWSPayload {
  account_id: string
  commission: string
  fee: string
  fee_currency: string
  id: string
  is_maker: boolean
  open_time: string
  close_time: string
  price: string
  quantity: string
  realized_pnl: string
  side: 'LONG' | 'SHORT'
  sl: string | null
  swap: string
  symbol: string
  tp: string | null
  trade_time: string
}

export interface TradeWSUpdate {
  event: 'created' | 'deleted'
  trade: TradeWSPayload
}

export interface BalanceWSUpdate {
  account_id: string
  available_balance: string
  balance: string
  currency: string
  locked_balance: string
}

export interface ConnectedMessage {
  type: 'connected'
  data: {
    client_id: string
  }
}

export interface SubscribedMessage {
  type: 'subscribed'
  channel: 'ticker' | 'orderbook' | 'trades' | 'klines'
  data: {
    symbol: string
    interval: string
  }
}

export interface KlineData {
  close: number
  high: number
  interval: string
  low: number
  open: number
  symbol: string
  time: number
  volume: number
}

export interface KlineUpdateMessage {
  type: 'update'
  channel: 'klines'
  data: KlineData
}

export type PublicWSMessage = ConnectedMessage | SubscribedMessage | KlineUpdateMessage
